import { ArrowRight, Play } from "lucide-react";
import AnimatedSection from "@/components/AnimatedSection";
import PropertyContact from "@/components/PropertyContact";
import { CONTACTS } from "@/data/contacts";

const IMAGES = {
  hero: "/images/seafood/facade.jpg",
  platter: "/images/stock/seafood_curry.jpg",
  ambience: "/images/stock/seafood_kerala.jpg",
};

const DISHES = [
  { name: "Prawns Butter Garlic", desc: "Scampi prepared in our own unique style — rich, buttery, and nothing like what you'll find anywhere else." },
  { name: "Masala Fry", desc: "An authentic Karwari preparation. Curry leaves, garlic, local spices — the kind of dish that defines this coast." },
  { name: "Ambus Tik Curry", desc: "A traditional curry also known as Jira Mire Ambat. Tangy, spiced, and deeply rooted in local Karwari cooking." },
  { name: "Dal Toy", desc: "One of our most popular dishes. We ferment coconut milk and prepare it the old way. People come here just for this." },
  { name: "Ghee Roast", desc: "Slow-roasted in a spice-rich ghee marinade. Bold flavours, traditional technique, no shortcuts." },
  { name: "Kalva Dab Dabbe", desc: "A traditional mussel preparation that's been on our menu since the beginning. Cooked the way it's always been." },
  { name: "Bangda Kit Kitey", desc: "Mackerel done the Karwari way — a traditional preparation that's become a signature of our kitchen." },
  { name: "And Many More", desc: "Butter Chicken, Hyderabadi Biryani, and a full non-veg and multi-cuisine menu. There's something for everyone." },
];

const FEATURES = [
  { label: "Serving Since", value: "1976" },
  { label: "Seating Capacity", value: "500" },
  { label: "Featured On", value: "History Channel & NDTV" },
  { label: "Recognition", value: "Top 200 & 75 Iconic" },
];

const VIDEOS = [
  { title: "Featured on History Channel", url: "https://www.youtube.com/watch?v=wL2WmR4H3RY", embedId: "wL2WmR4H3RY" },
  { title: "Featured on NDTV Good Times", url: "https://www.youtube.com/watch?v=udOfR8u7xtM", embedId: "udOfR8u7xtM" },
];

export default function AmrutSeafoodPage() {
  return (
    <div data-testid="amrut-seafood-page" className="bg-ivory">
      {/* ─── HERO ─── */}
      <section className="relative h-screen w-full overflow-hidden">
        <img src={IMAGES.hero} alt="Amrut Seafood" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/85 via-charcoal/40 to-charcoal/40 pointer-events-none" />
        <div className="relative z-10 flex flex-col justify-end h-full p-8 md:p-16 lg:p-24 pb-20 md:pb-28">
          <AnimatedSection delay={0.2}><p className="overline-text text-gold/80 mb-4">Since 1976</p></AnimatedSection>
          <AnimatedSection delay={0.4}><h1 className="font-serif text-5xl md:text-6xl lg:text-8xl text-ivory font-light tracking-tighter leading-none mb-6 drop-shadow-lg">Amrut Seafood</h1></AnimatedSection>
          <AnimatedSection delay={0.6}><p className="text-sm md:text-base text-ivory/85 max-w-lg leading-relaxed">Fresh fish, no additives, and recipes that haven't changed in nearly 50 years. 500 seats. Featured on History Channel and NDTV Good Times.</p></AnimatedSection>
        </div>
      </section>

      {/* ─── QUICK FACTS ─── */}
      <section className="py-10 md:py-14 px-6 md:px-10 lg:px-16 border-b border-charcoal/10 bg-ivory">
        <div className="max-w-[1200px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
          {FEATURES.map((f, i) => (
            <AnimatedSection key={i} delay={i * 0.1}>
              <div className="text-center">
                <p className="font-serif text-3xl md:text-4xl text-charcoal font-light tracking-tight">{f.value}</p>
                <p className="text-xs text-charcoal/50 tracking-[0.15em] uppercase mt-2">{f.label}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* ─── STORY ─── */}
      <section className="py-16 md:py-24 px-6 md:px-10 lg:px-16 bg-ivory">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <AnimatedSection>
            <div className="w-[60px] h-[1px] bg-gold/60 mb-8" />
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-charcoal font-light tracking-tight leading-tight mb-8">100% natural.<br />No additives. Ever.</h2>
            <p className="text-base text-charcoal/65 leading-relaxed mb-6">
              We buy our fish locally from Karwar's fishermen. The taste comes from the estuary —
              the brackish water here gives the fish a flavour you won't find anywhere else.
              All our taste enhancers are natural. We don't add anything artificial to the food.
              That's been the rule since 1976.
            </p>
            <p className="text-base text-charcoal/65 leading-relaxed">
              The restaurant is big — 500 seats across multiple sections. We have three exclusive
              family seating areas, a general section, and an AC section for elderly guests
              who prefer not to climb stairs. Full bar available.
            </p>
          </AnimatedSection>
          <AnimatedSection delay={0.2} className="overflow-hidden h-[50vh] md:h-[60vh]">
            <img src={IMAGES.platter} alt="Seafood platter" className="w-full h-full object-cover" />
          </AnimatedSection>
        </div>
      </section>

      {/* ─── SIGNATURE DISHES ─── */}
      <section data-testid="seafood-dishes" className="py-16 md:py-24 px-6 md:px-10 lg:px-16 bg-ivory">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection><p className="overline-text text-charcoal/50 mb-6">What to Order</p><h2 className="font-serif text-4xl md:text-5xl text-charcoal font-light tracking-tight mb-20">Our Signature Dishes</h2></AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-0">
            {DISHES.map((dish, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <div className="border-t border-charcoal/15 py-8 md:py-10">
                  <h4 className="font-serif text-xl md:text-2xl text-charcoal font-light tracking-tight mb-3">{dish.name}</h4>
                  <p className="text-sm text-charcoal/60 leading-relaxed">{dish.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FEATURED ON ─── */}
      <section data-testid="seafood-featured" className="py-16 md:py-24 px-6 md:px-10 lg:px-16 bg-ivory">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection>
            <p className="overline-text text-charcoal/50 mb-6">As Seen On</p>
            <h2 className="font-serif text-4xl md:text-5xl text-charcoal font-light tracking-tight mb-8">Featured on National Television</h2>
            <p className="text-base text-charcoal/65 leading-relaxed max-w-2xl mb-16">
              Amrut Seafood Restaurant has been featured on History Channel and NDTV Good Times.
              Listed among India's 75 iconic restaurants and recognized by Times of India as one of
              India's top 200 restaurants.
            </p>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {VIDEOS.map((video, i) => (
              <AnimatedSection key={i} delay={i * 0.15}>
                <div className="relative aspect-video bg-charcoal/5 overflow-hidden">
                  <iframe
                    src={`https://www.youtube.com/embed/${video.embedId}`}
                    title={video.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
                <p className="text-xs text-charcoal/50 mt-3 tracking-wide">{video.title}</p>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── AMBIENCE IMAGE ─── */}
      <section className="relative w-full h-[50vh] md:h-[60vh] overflow-hidden">
        <img src={IMAGES.ambience} alt="Restaurant ambience" className="w-full h-full object-cover" />
      </section>

      {/* ─── CONTACT ─── */}
      <section className="py-12 md:py-16 px-6 md:px-10 lg:px-16 bg-ivory">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection><PropertyContact contact={CONTACTS.seafood} variant="light" /></AnimatedSection>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="py-10 md:py-14 px-6 md:px-10 lg:px-16 bg-ivory text-center">
        <div className="max-w-[700px] mx-auto">
          <AnimatedSection><h2 className="font-serif text-4xl md:text-5xl text-charcoal font-light tracking-tight mb-6">Reserve a Table</h2></AnimatedSection>
          <AnimatedSection delay={0.1}><p className="text-sm text-charcoal/55 leading-relaxed mb-10 max-w-md mx-auto">Call or WhatsApp us. Walk-ins are welcome too, but a reservation helps — especially on weekends.</p></AnimatedSection>
          <AnimatedSection delay={0.2}>
            <a href={`https://wa.me/${CONTACTS.seafood.whatsappRaw}?text=I'd%20like%20to%20reserve%20a%20table%20at%20Amrut%20Seafood`} target="_blank" rel="noopener noreferrer" data-testid="reserve-table-btn" className="inline-flex items-center gap-3 px-10 py-4 border border-charcoal/25 text-charcoal text-xs tracking-[0.2em] uppercase font-medium hover:bg-charcoal hover:text-ivory transition-all duration-500">
              Reserve via WhatsApp <ArrowRight size={14} strokeWidth={1.5} />
            </a>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
